import React, { useState } from 'react';
import { ExternalLink, FileDown, FileText, Loader2 } from 'lucide-react';
import { getBlob, ref, storage } from '../firebaseUtils.js';

const AttachmentCard = ({
  href,
  label,
  name,
  icon: Icon = FileText,
  iconBackgroundColor = '#e0f2fe',
  iconColor = '#0284c7',
}) => {
  const [isDownloading, setIsDownloading] = useState(false);

  if (!href) return null;

  const isFirebaseStorageUrl = (url) =>
    typeof url === 'string' && (
      url.includes('firebasestorage.googleapis.com') ||
      url.includes('storage.googleapis.com') ||
      url.includes('nexrise-internal.firebasestorage.app')
    );

  const handleDownload = async (event) => {
    event.preventDefault();
    if (isDownloading) return;

    setIsDownloading(true);
    try {
      let blob;

      if (isFirebaseStorageUrl(href)) {
        blob = await getBlob(ref(storage, href));
      } else {
        const response = await fetch(href);
        if (!response.ok) {
          throw new Error(`Download failed with status ${response.status}`);
        }
        blob = await response.blob();
      }

      const objectUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = objectUrl;
      link.download = name || label || 'download';
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(objectUrl);
    } catch (error) {
      console.error('Attachment download failed:', error);
      window.open(href, '_blank', 'noopener,noreferrer');
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <div style={attachmentCardStyle}>
      <div style={{ ...attachmentIconStyle, backgroundColor: iconBackgroundColor, color: iconColor }}>
        <Icon size={20} />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={attachmentLabelStyle}>{label}</div>
        <div style={attachmentNameStyle}>{name}</div>
      </div>
      <div style={attachmentActionsStyle}>
        <a
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          style={actionButtonStyle}
          title={`Open ${label}`}
        >
          <ExternalLink size={16} color="#6b7280" />
        </a>
        <a
          href={href}
          onClick={handleDownload}
          style={actionButtonStyle}
          title={`Download ${label}`}
        >
          {isDownloading ? <Loader2 size={16} color="#6b7280" className="animate-spin" /> : <FileDown size={16} color="#6b7280" />}
        </a>
      </div>
    </div>
  );
};

const attachmentCardStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: '10px',
  padding: '12px',
  border: '1px solid #e2e8f0',
  borderRadius: '8px',
  backgroundColor: '#f8fafc',
  color: '#374151',
  transition: 'all 0.2s ease-in-out',
};

const attachmentIconStyle = {
  padding: '8px',
  borderRadius: '6px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
};

const attachmentLabelStyle = {
  fontSize: '11px',
  fontWeight: '600',
  color: '#64748b',
  textTransform: 'uppercase',
  marginBottom: '2px',
};

const attachmentNameStyle = {
  fontSize: '13px',
  fontWeight: 'bold',
  color: '#1e293b',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  display: 'block',
};

const attachmentActionsStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
};

const actionButtonStyle = {
  width: '32px',
  height: '32px',
  borderRadius: '6px',
  border: '1px solid #e2e8f0',
  backgroundColor: '#fff',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  textDecoration: 'none',
  flexShrink: 0,
};

export default AttachmentCard;
